import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SiteDocAiSettings } from '@symphenia/shared-types'
import { bestaetige } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDateTime } from '@/lib/format-date'
import { Abschnitt } from '@/lib/speichern'
import { fetchSiteDocAiSettings, setSiteDocAiSettings } from '../api/site-docs.api'

/**
 * AP-46 21.4 — **„Diktat mit KI auswerten"** (nur mit gebuchtem `SITE_DOCS.AI`). Einschalten ist eine
 * Handlung mit bestätigtem Hinweis — Text, Kästchen und Fassung kommen vom Server; Ausschalten nimmt die
 * Zustimmung zurück. Ein Schalter für alle Arten.
 */
export function KiEinstellung() {
  const { t } = useTranslation()
  const [stand, setStand] = useState<SiteDocAiSettings | null>(null)
  const [gelesen, setGelesen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    fetchSiteDocAiSettings()
      .then(setStand)
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [t])

  const setze = async (enabled: boolean) => {
    if (!enabled && !(await bestaetige(t('siteDocs.settings.ai.offConfirm'), { ok: t('siteDocs.settings.ai.off'), gefaehrlich: true }))) return
    setFehler(null)
    setLaeuft(true)
    try {
      setStand(await setSiteDocAiSettings({ enabled, accepted: enabled ? gelesen : undefined }))
      setGelesen(false)
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Abschnitt titel={t('siteDocs.settings.ai.title')}>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">{t('siteDocs.settings.ai.intro')}</p>
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        {stand && !stand.aiAvailable && <p className="rounded-md bg-amber-100 px-3 py-2 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('siteDocs.settings.ai.notAvailable')}</p>}
        {stand?.enabled ? (
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {t('siteDocs.settings.ai.onSince', { datum: stand.acceptedAt ? fmtDateTime(stand.acceptedAt) : '—', name: stand.acceptedByName ?? '—' })}
            </span>
            <button type="button" disabled={laeuft} onClick={() => void setze(false)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50">
              {t('siteDocs.settings.ai.off')}
            </button>
          </div>
        ) : (
          stand && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <p>{stand.consentText}</p>
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-0.5" checked={gelesen} onChange={(e) => setGelesen(e.target.checked)} />
                <span>{stand.consentCheckbox}</span>
              </label>
              <button type="button" disabled={!gelesen || laeuft} onClick={() => void setze(true)} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
                {t('siteDocs.settings.ai.on')}
              </button>
              <p className="text-xs text-muted-foreground">{t('siteDocs.settings.ai.version', { fassung: stand.consentVersion })}</p>
            </div>
          )
        )}
      </div>
    </Abschnitt>
  )
}
